<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3 class="mb-0">
          Imtihon dasturlari va taqdimotlar
        </h3>
        <div>
          <b-button
            v-if="isInnovation"
            variant="success"
            :disabled="!selectedPeriodId || exporting"
            @click="exportExcel"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            Excel yuklab olish
          </b-button>
          <b-button
            v-if="canManage"
            variant="primary"
            @click="openForm()"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            Yaratish
          </b-button>
        </div>
      </div>

      <b-row class="mb-2">
        <b-col md="4">
          <b-form-group
            label="Qabul yili"
            class="mb-md-0"
          >
            <v-select
              v-model="selectedPeriodId"
              :reduce="option => option.id"
              :options="periods"
              :get-option-label="periodLabel"
              :clearable="false"
              placeholder="Yilni tanlang"
            />
            <small
              v-if="isInnovation"
              class="text-muted"
            >
              Excel tanlangan yilning barcha tashkilotlari bo‘yicha yuklanadi.
            </small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Tashkilot"
            class="mb-0"
          >
            <v-select
              v-model="selectedOrganizationId"
              :reduce="option => option.id"
              :options="organizationOptions"
              :get-option-label="organizationLabel"
              placeholder="Barcha tashkilotlar"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        :busy="loading"
        responsive
        striped
        show-empty
        empty-text="Ma’lumot topilmadi"
      >
        <template #cell(files)="data">
          <b-link
            v-for="file in data.item.files"
            :key="file.id"
            :href="file.file"
            target="_blank"
            class="d-block"
          >
            {{ file.original_name }}
          </b-link>
        </template>
        <template #cell(organization_name)="data">
          {{ data.value ? $langName(data.value) : '-' }}
        </template>
        <template #cell(admission_year)="data">
          {{ data.value || '-' }}
        </template>
        <template #cell(status)="data">
          <b-badge :variant="data.value === 200 ? 'light-success' : 'light-warning'">
            {{ statusLabel(data.value) }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <template v-if="canManage">
            <feather-icon
              icon="EditIcon"
              class="cursor-pointer mr-1"
              @click="openForm(data.item)"
            />
            <feather-icon
              icon="TrashIcon"
              class="cursor-pointer text-danger"
              @click="removeItem(data.item)"
            />
          </template>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="formVisible"
      :title="form.id ? 'Tahrirlash' : 'Yangi e’lon'"
      hide-footer
      size="lg"
    >
      <validation-observer ref="formObserver">
        <b-form @submit.prevent="save">
          <b-form-group label="Qabul yili">
            <validation-provider
              #default="{ errors }"
              name="Qabul yili"
              rules="required"
            >
              <v-select
                v-model="form.period"
                :reduce="option => option.id"
                :options="periods"
                :get-option-label="periodLabel"
                :clearable="false"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="E’lon sarlavhasi">
            <validation-provider
              #default="{ errors }"
              name="E’lon sarlavhasi"
              rules="required"
            >
              <b-form-input
                v-model="form.title"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group label="Izohi">
            <validation-provider
              #default="{ errors }"
              name="Izohi"
              rules="required"
            >
              <b-form-textarea
                v-model="form.description"
                rows="5"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Status">
            <validation-provider
              #default="{ errors }"
              name="Status"
              rules="required"
            >
              <v-select
                v-model="form.status"
                :reduce="option => option.id"
                :options="statusOptions"
                label="name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div
            v-for="file in form.files"
            :key="file.id"
            class="d-flex justify-content-between align-items-center border rounded p-1 mb-50"
          >
            <b-link
              :href="file.file"
              target="_blank"
            >
              {{ file.original_name }}
            </b-link>
            <b-button
              v-if="canManage"
              size="sm"
              variant="outline-danger"
              @click="removeAttachment(file)"
            >
              O‘chirish
            </b-button>
          </div>

          <b-form-group label="Fayllar">
            <validation-provider
              #default="{ errors }"
              name="Fayllar"
              :rules="form.id ? '' : 'required'"
            >
              <b-form-file
                v-model="form.newFiles"
                multiple
                accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.zip"
                placeholder="Fayllarni tanlang..."
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div class="text-right">
            <b-button
              type="submit"
              variant="primary"
              :disabled="saving"
            >
              Saqlash
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCol, BForm, BFormFile, BFormGroup, BFormInput, BFormTextarea, BLink, BModal,
  BRow, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@/libs/validations/validations'
import module from '@/store/exam-program-presentation'
import store from '@/store'
import axios from '@/libs/axios'

const freshForm = () => ({
  id: null,
  title: '',
  description: '',
  status: 100,
  period: null,
  files: [],
  newFiles: [],
})

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BLink,
    BModal,
    BRow,
    BTable,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      required,
      formVisible: false,
      loading: false,
      saving: false,
      exporting: false,
      periods: [],
      selectedPeriodId: null,
      selectedOrganizationId: null,
      form: freshForm(),
      fields: [
        { key: 'organization_name', label: 'Tashkilot' },
        { key: 'admission_year', label: 'Qabul yili' },
        { key: 'title', label: 'E’lon sarlavhasi' },
        { key: 'description', label: 'Izohi' },
        { key: 'status', label: 'Status' },
        { key: 'files', label: 'Fayllar' },
        { key: 'actions', label: 'Amallar' },
      ],
      statusOptions: [
        { id: 100, name: 'Qoralama' },
        { id: 200, name: 'Faol' },
      ],
    }
  },
  computed: {
    canManage() {
      return this.$store.state.auth.user.role_id === this.$store.state.staticModule.roleUniversity
    },
    isInnovation() {
      return this.$store.state.auth.user.role_id === this.$store.state.staticModule.roleInnovation
    },
    items() {
      const { items } = this.$store.state.examProgramPresentation
      if (!this.selectedOrganizationId) return items
      return items.filter(item => String(item.organization) === String(this.selectedOrganizationId))
    },
    organizationOptions() {
      const organizations = new Map()
      this.$store.state.examProgramPresentation.items.forEach(item => {
        if (!organizations.has(item.organization)) {
          organizations.set(item.organization, { id: item.organization, name: item.organization_name })
        }
      })
      return Array.from(organizations.values()).sort((first, second) => (
        this.organizationLabel(first).localeCompare(this.organizationLabel(second))
      ))
    },
  },
  watch: {
    selectedPeriodId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.selectedOrganizationId = null
        this.load()
      }
    },
  },
  created() {
    if (!store.hasModule('examProgramPresentation')) store.registerModule('examProgramPresentation', module)
    this.loadPeriods()
  },
  methods: {
    getList(data) {
      const { results, data: nestedData } = data
      const list = results || nestedData || data
      return Array.isArray(list) ? list : []
    },
    loadPeriods() {
      axios.get('/periods/', { params: { limit: 1200 } }).then(response => {
        this.periods = this.getList(response.data).sort((first, second) => second.admission_year - first.admission_year)
        this.selectedPeriodId = this.periods.length ? this.periods[0].id : null
        if (!this.selectedPeriodId) this.load()
      })
    },
    periodLabel(option) {
      return option ? String(option.admission_year) : ''
    },
    organizationLabel(option) {
      if (!option || !option.name) return '-'
      return typeof option.name === 'string' ? option.name : this.$langName(option.name)
    },
    exportExcel() {
      if (!this.selectedPeriodId || this.exporting) return
      this.exporting = true
      this.$store.dispatch('examProgramPresentation/exportExcel', this.selectedPeriodId).then(response => {
        const selectedPeriod = this.periods.find(period => period.id === this.selectedPeriodId)
        const year = selectedPeriod ? selectedPeriod.admission_year : this.selectedPeriodId
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = `imtihon-dasturlari-va-taqdimotlar-${year}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }).catch(error => this.$errorSwal(this, error)).finally(() => {
        this.exporting = false
      })
    },
    statusLabel(status) {
      const option = this.statusOptions.find(item => item.id === status)
      return option ? option.name : status
    },
    load() {
      this.loading = true
      this.$store.dispatch('examProgramPresentation/list', {
        manage: true,
        limit: 1200,
        period: this.selectedPeriodId || undefined,
      }).finally(() => {
        this.loading = false
      })
    },
    openForm(item = null) {
      this.form = item
        ? { ...item, files: [...item.files], newFiles: [] }
        : { ...freshForm(), period: this.selectedPeriodId }
      this.formVisible = true
    },
    save() {
      this.$refs.formObserver.validate().then(valid => {
        if (!valid) return
        this.saving = true
        this.$store.dispatch('examProgramPresentation/save', this.form).then(() => {
          this.formVisible = false
          this.load()
        }).catch(error => this.$errorSwal(this, error)).finally(() => {
          this.saving = false
        })
      })
    },
    removeItem(item) {
      this.$bvModal.msgBoxConfirm(`“${item.title}” e’lonini o‘chirasizmi?`).then(confirmed => {
        if (confirmed) this.$store.dispatch('examProgramPresentation/remove', item.id).then(this.load)
      })
    },
    removeAttachment(file) {
      this.$bvModal.msgBoxConfirm(`“${file.original_name}” faylini o‘chirasizmi?`).then(confirmed => {
        if (!confirmed) return
        this.$store.dispatch('examProgramPresentation/removeFile', {
          itemId: this.form.id,
          fileId: file.id,
        }).then(() => {
          this.form.files = this.form.files.filter(item => item.id !== file.id)
        }).catch(error => this.$errorSwal(this, error))
      })
    },
  },
}
</script>
